import { useState } from 'react'
import { useStore } from '../store/store'
import { kellyFraction, positionSize, recommendedStake, riskOfRuin } from '../lib/math'
import { fmtCurrency, fmtPct } from '../lib/format'
import { Button, Card, NumberField, PageHead, Pill, SelectField } from '../components/ui'
import type { KellyMode } from '../domain/types'

export function PositionSizing() {
  const risk = useStore((s) => s.risk)
  const [bankroll, setBankroll] = useState(risk.bankroll)
  const [entry, setEntry] = useState(100)
  const [stop, setStop] = useState(95)
  const [riskPct, setRiskPct] = useState(risk.maxRiskPerTradePct)
  const [maxPos, setMaxPos] = useState(risk.maxSinglePositionPct)

  const sizing = positionSize({ bankroll, entryPrice: entry, stopPrice: stop, riskPctOfBankroll: riskPct, maxPositionPctOfBankroll: maxPos })

  const [prob, setProb] = useState(0.55)
  const [odds, setOdds] = useState(2)
  const [mode, setMode] = useState<KellyMode>(risk.kellyMode)
  const stake = recommendedStake({ bankroll, prob, decimalOdds: odds, mode, maxStakePct: maxPos })
  const fullK = kellyFraction(prob, odds)

  const ror = riskOfRuin({
    bankroll,
    winProb: prob,
    stakeFraction: Math.max(0.001, stake.fraction || 0.01),
    payoutMultiple: odds,
    bets: 100,
    ruinThresholdPct: 0.5,
    simulations: 1500,
    seed: 21,
  })

  return (
    <div className="stack">
      <PageHead title="Position Sizing">
        Size from your stop and your bankroll, never from conviction. The right size makes a loss boring.
      </PageHead>

      <div className="grid cols-2">
        <Card title="Stock / fixed-stop sizing">
          <div className="grid cols-2">
            <NumberField label="Bankroll" value={bankroll} onChange={setBankroll} step={100} />
            <NumberField label="Risk per trade" value={riskPct} onChange={setRiskPct} step={0.005} min={0} max={0.1} hint="fraction" />
            <NumberField label="Entry price" value={entry} onChange={setEntry} step={0.01} />
            <NumberField label="Stop price" value={stop} onChange={setStop} step={0.01} />
            <NumberField label="Max position %" value={maxPos} onChange={setMaxPos} step={0.01} min={0} max={1} hint="fraction" />
          </div>
        </Card>

        <Card title="Result">
          {sizing.valid ? (
            <>
              <div className="stat-value">{sizing.shares} <span className="muted" style={{ fontSize: 14 }}>shares</span></div>
              <div className="kv"><span className="k">Position value</span><span className="v">{fmtCurrency(sizing.positionValue)}</span></div>
              <div className="kv"><span className="k">Dollars at risk</span><span className="v neg">{fmtCurrency(sizing.dollarsAtRisk)}</span></div>
              <div className="kv"><span className="k">Risk per share</span><span className="v">{fmtCurrency(sizing.riskPerShare)}</span></div>
              <div className="kv"><span className="k">% of bankroll</span><span className="v">{fmtPct(sizing.pctOfBankroll, 1)}</span></div>
              {sizing.cappedByExposure && <Pill tone="amber">Capped by max position size — your stop is too tight for this risk %</Pill>}
            </>
          ) : (
            <div className="empty">Stop must differ from entry to compute risk.</div>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Bet stake (Kelly)">
          <div className="grid cols-2">
            <NumberField label="Win probability" value={prob} onChange={setProb} step={0.01} min={0} max={1} />
            <NumberField label="Decimal odds" value={odds} onChange={setOdds} step={0.01} min={1.01} />
            <SelectField
              label="Kelly mode"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'full', label: 'Full Kelly' },
                { value: 'half', label: 'Half Kelly' },
                { value: 'quarter', label: 'Quarter Kelly' },
                { value: 'flat', label: 'Flat 1%' },
              ]}
            />
          </div>
          <div className="kv"><span className="k">Full Kelly fraction</span><span className="v">{fmtPct(fullK, 2)}</span></div>
          <div className="kv"><span className="k">Recommended fraction</span><span className="v">{fmtPct(stake.fraction, 2)}</span></div>
          <div className="kv"><span className="k">Recommended stake</span><span className="v">{fmtCurrency(stake.stake)}</span></div>
          {!stake.edgePresent && <Pill tone="red">No edge — stake is zero. Skip.</Pill>}
        </Card>

        <Card title="Risk of ruin" subtitle="Monte Carlo: 100 bets at this fraction, ruin = losing half the bankroll">
          <div className="stat-value" style={{ color: ror.riskOfRuinPct > 20 ? 'var(--danger)' : ror.riskOfRuinPct > 5 ? 'var(--warn)' : 'var(--accent)' }}>
            {ror.riskOfRuinPct.toFixed(1)}%
          </div>
          <div className="kv"><span className="k">Median ending bankroll</span><span className="v">{fmtCurrency(ror.medianEnding)}</span></div>
          <div className="kv"><span className="k">5th percentile</span><span className="v neg">{fmtCurrency(ror.p5Ending)}</span></div>
          <div className="kv"><span className="k">95th percentile</span><span className="v pos">{fmtCurrency(ror.p95Ending)}</span></div>
          <div className="kv"><span className="k">Avg max drawdown</span><span className="v">{ror.avgMaxDrawdownPct.toFixed(1)}%</span></div>
          <Button size="sm" onClick={() => setMode('quarter')}>Use safer quarter-Kelly</Button>
        </Card>
      </div>
    </div>
  )
}
